/// TypeScript types for element property editing in the Properties panel.
/// Covers both BPMN element properties and Node element properties.

// ---------------------------------------------------------------------------
// BPMN element property schemas
// ---------------------------------------------------------------------------

/** Discriminated BPMN element types matching the editor model. */
export type BPMNElementType =
  | "startEvent"
  | "endEvent"
  | "timerEvent"
  | "messageEvent"
  | "signalEvent"
  | "errorEvent"
  | "terminateEvent"
  | "boundaryEvent"
  | "task"
  | "serviceTask"
  | "userTask"
  | "scriptTask"
  | "sendTask"
  | "receiveTask"
  | "exclusiveGateway"
  | "parallelGateway"
  | "inclusiveGateway"
  | "eventBasedGateway"
  | "subprocess"
  | "callActivity"
  | "pool"
  | "lane"
  | "sequenceFlow"
  | "messageFlow";

/** Properties for a BPMN element, covering common and type-specific fields. */
export interface BPMNElementProperties {
  id: string;
  type: BPMNElementType;
  name: string;
  // Task-specific
  taskType?: string;
  retries?: number;
  // Service task
  jobType?: string;
  jobCustomHeaders?: Record<string, string>;
  // User task
  formKey?: string;
  assignee?: string;
  candidateGroups?: string;
  // Script task
  scriptLanguage?: string;
  scriptBody?: string;
  // Timer event
  timerType?: "duration" | "date" | "cycle";
  timerExpression?: string;
  // Sequence flow
  conditionExpression?: string;
  isDefault?: boolean;
  // Multi-instance
  isMultiInstance?: boolean;
  isSequential?: boolean;
  collectionExpression?: string;
  elementVariable?: string;
  // IO Mappings
  inputMappings?: { source: string; target: string }[];
  outputMappings?: { source: string; target: string }[];
}

// ---------------------------------------------------------------------------
// Node element property schemas
// ---------------------------------------------------------------------------

/** Socket data type for node editor. */
export type NodeSocketType =
  | "number"
  | "string"
  | "boolean"
  | "any"
  | "flow"
  | "array"
  | "object"
  | "trigger";

/** Properties for a Node element. */
export interface NodeElementProperties {
  id: string;
  nodeType: string;
  category: string;
  name: string;
  inputs: {
    name: string;
    type: NodeSocketType;
    defaultValue?: string;
    connected: boolean;
  }[];
  outputs: {
    name: string;
    type: NodeSocketType;
  }[];
  settings?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Severity helpers for console event display
// ---------------------------------------------------------------------------

/** Event severity level for console display. */
export type EventSeverity = "info" | "warning" | "error";

/** Map engine event types to severity for console coloring. */
export function getEventSeverity(eventType: string): EventSeverity {
  const errorEvents = ["error", "incidentRaised", "jobFailed"];
  const warningEvents = ["jobTimedOut", "instanceSuspended", "warning"];

  if (errorEvents.includes(eventType)) return "error";
  if (warningEvents.includes(eventType)) return "warning";
  return "info";
}
